import React, { useState, useEffect } from 'react';
import { ConfiguratorState } from '../../types';
import { debugError } from '../../utils/debug';
import {
  mapApiBreakdownToPriceLines,
  PriceLine,
} from '../../services/summaryPriceCalculations';
import { generateOfferPdf } from '../../services/offerPdf';
import { useDiscountCode } from '../../hooks/useDiscountCode';
import { EmailService, OfferEmailData } from '../../services/emailService';
import { priceService } from '../../services/priceService';
import OfferSuccessModal from '../UI/OfferSuccessModal';
import AlreadyCreatedModal from '../UI/AlreadyCreatedModal';
import Toast from '../UI/Toast';
import PriceBreakdown from '../UI/PriceBreakdown';
import OfferForm, { ContactFormData } from './OfferForm';
import { formatDisplayPrice } from '../../utils/priceFormat';

interface SummaryProps {
  state: ConfiguratorState;
  updateState: (newState: Partial<ConfiguratorState>) => void;
  onBack?: () => void;
  /** Von der Preisleiste gesteuert — öffnet das Kontaktformular. */
  showOfferForm?: boolean;
  onShowOfferForm?: (open: boolean) => void;
  showGross?: boolean;
}

export default function Summary({
  state,
  updateState,
  showOfferForm,
  onShowOfferForm,
  showGross = true,
}: SummaryProps) {
  const [internalForm, setInternalForm] = useState(false);
  const showForm = showOfferForm ?? internalForm;
  const setShowForm = onShowOfferForm ?? setInternalForm;

  const [apiBreakdown, setApiBreakdown] = useState<any[]>([]);
  const [apiPrice, setApiPrice] = useState<number | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);

  const [formData, setFormData] = useState<ContactFormData>({
    type: 'private',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    technicalDrawing: false,
    privacyAccepted: false,
  });
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ show: false, message: '', type: 'success' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAlreadyCreatedModal, setShowAlreadyCreatedModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastOfferNumber, setLastOfferNumber] = useState('');
  const [offerCreated, setOfferCreated] = useState(false);

  const discount = useDiscountCode({
    initial: state.appliedDiscount || null,
    onSync: (d) => updateState({ appliedDiscount: d }),
    getCurrentNetPrice: () => calculateTotalNetPrice(),
    onSuccess: (message) => setToast({ show: true, message, type: 'success' }),
  });
  const {
    discountCode,
    setDiscountCode,
    appliedDiscount,
    discountError,
    isValidatingDiscount,
    validateDiscountCode,
    removeDiscountCode,
    calculateCurrentDiscountAmount,
  } = discount;

  useEffect(() => {
    if (!state.product || !state.glassType) {
      setApiBreakdown([]);
      setApiPrice(null);
      setPriceError(null);
      return;
    }
    (async () => {
      try {
        setPriceLoading(true);
        setPriceError(null);
        const result = await priceService.calculatePrice({
          product: state.product,
          dimensions: {
            width: state.dimensions.width,
            length: state.dimensions.length,
            height: state.dimensions.height,
            diameter: state.dimensions.diameter,
          },
          glassType: state.glassType as 'double' | 'triple',
          accessories: state.accessories || [],
          ralColorOutside: state.ralColor,
          ralColorInside: state.ralColorInside,
          controlType: state.controlType,
        });
        setApiBreakdown(result.breakdown || []);
        setApiPrice(result.total_price);
      } catch (err) {
        debugError('Fehler beim Laden des Backend-Preises:', err);
        setApiBreakdown([]);
        setApiPrice(null);
        setPriceError(
          err instanceof Error && err.message
            ? err.message
            : 'Preis konnte nicht vom Backend geladen werden.',
        );
      } finally {
        setPriceLoading(false);
      }
    })();
  }, [
    state.product,
    state.dimensions,
    state.glassType,
    state.accessories,
    state.ralColor,
    state.ralColorInside,
    state.controlType,
  ]);

  const priceLines: PriceLine[] = mapApiBreakdownToPriceLines(
    apiBreakdown,
    state.product,
  );
  const usedApiBreakdown = apiPrice != null && priceLines.length > 0;

  const calculateTotalNetPrice = () => {
    if (apiPrice != null) return apiPrice;
    return 0;
  };

  const calculateFinalPrice = () =>
    calculateTotalNetPrice() - calculateCurrentDiscountAmount();

  const formatPrice = (price: number) => formatDisplayPrice(price, showGross);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const generatePDF = () =>
    generateOfferPdf({
      product: state.product,
      dimensions: state.dimensions,
      modules: state.modules,
      glassType: state.glassType,
      accessories: state.accessories || [],
      ralColor: state.ralColor,
      ralColorInside: state.ralColorInside,
      customer: {
        type: formData.type,
        firstName: formData.firstName,
        lastName: formData.lastName,
        street: formData.street,
        postalCode: formData.postalCode,
        city: formData.city,
        companyName: formData.companyName,
      },
      useBackendBreakdown: usedApiBreakdown,
      breakdown: apiBreakdown,
      netPrice: calculateTotalNetPrice(),
      discount: appliedDiscount,
      discountAmount: calculateCurrentDiscountAmount(),
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (offerCreated) {
      setShowAlreadyCreatedModal(true);
      return;
    }
    if (priceError || apiPrice == null) {
      setToast({
        show: true,
        message: priceError || 'Ohne Backend-Preis kann kein Angebot erstellt werden.',
        type: 'error',
      });
      return;
    }
    if (!formData.privacyAccepted) {
      setToast({
        show: true,
        message: 'Bitte akzeptieren Sie die Datenschutzerklärung.',
        type: 'error',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const { pdf, offerNumber } = await generatePDF();
      const pdfBlob = pdf.output('blob');
      const pdfBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(pdfBlob);
      });

      const emailData: OfferEmailData = {
        customerData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          street: formData.street,
          postalCode: formData.postalCode,
          city: formData.city,
          type: formData.type,
          companyName: formData.companyName,
        },
        productConfig: {
          product: state.product,
          dimensions: state.dimensions,
          glassType: state.glassType,
          accessories: state.accessories,
          totalPrice: calculateFinalPrice(),
          appliedDiscount: appliedDiscount
            ? { ...appliedDiscount, amount: calculateCurrentDiscountAmount() }
            : null,
        },
        services: {
          installation: false,
          measurement: false,
          technicalDrawing: formData.technicalDrawing,
        },
        offerNumber,
        pdfBase64,
      };

      const emailService = new EmailService();
      const [saveResult, customerEmailResult, salesEmailResult] = await Promise.all(
        [
          emailService.saveOffer(emailData),
          emailService.sendCustomerEmail(emailData),
          emailService.sendSalesEmail(emailData),
        ],
      );

      const backendOk = !!saveResult;
      const mailsOk = !!customerEmailResult && !!salesEmailResult;
      if (!backendOk && !mailsOk) {
        throw new Error(
          'Das Angebot konnte nicht übermittelt werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        );
      }
      pdf.save(`Angebot_${offerNumber}.pdf`);
      setLastOfferNumber(offerNumber);
      setOfferCreated(true);
      setShowForm(false);
      setShowSuccessModal(true);
      if (!(backendOk && mailsOk)) {
        setToast({
          show: true,
          message: !backendOk
            ? 'PDF erstellt und Vertrieb informiert. Interne Speicherung hatte einen Fehler — wir kümmern uns.'
            : 'Angebot gespeichert und PDF heruntergeladen. Beim E-Mail-Versand gab es ein Problem — wir melden uns telefonisch.',
          type: 'error',
        });
      }
    } catch (err) {
      debugError('Fehler beim Erstellen des Angebots:', err);
      setToast({
        show: true,
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestart = () => window.location.reload();

  const finalPrice = calculateFinalPrice();

  const formatLineAmount = (net: number | null) =>
    net == null ? 'inklusive' : formatPrice(net);

  return (
    <div>
      {!showForm ? (
        priceLoading ? (
          <div className="py-16 max-w-lg">
            <p className="sv-caps text-[11px] text-ink-3 mb-3">Preis</p>
            <p className="sv-serif text-ink-2">Preis wird vom Backend geladen …</p>
          </div>
        ) : priceError || apiPrice == null ? (
          <div className="py-16 max-w-lg">
            <p className="sv-caps text-[11px] text-ink-3 mb-3">Preis nicht verfügbar</p>
            <p className="sv-serif text-ink-2 mb-4">
              {priceError ||
                'Der Preis konnte nicht vom Backend geladen werden. Ein Angebot ist erst möglich, wenn der Preis steht.'}
            </p>
          </div>
        ) : (
        <PriceBreakdown
          priceLines={priceLines}
          finalPrice={finalPrice}
          showGross={showGross}
          formatPrice={formatPrice}
          formatLineAmount={formatLineAmount}
          discountCode={appliedDiscount?.code}
          discountAmount={calculateCurrentDiscountAmount()}
          onRequestOffer={() => setShowForm(true)}
        />
        )
      ) : (
        <OfferForm
          formData={formData}
          onFormChange={handleInputChange}
          onCheckboxChange={handleCheckboxChange}
          onTypeChange={(type) => setFormData((p) => ({ ...p, type }))}
          onSubmit={handleSubmit}
          onBack={() => setShowForm(false)}
          isSubmitting={isSubmitting}
          offerCreated={offerCreated}
          priceLines={priceLines}
          finalPrice={finalPrice}
          showGross={showGross}
          formatPrice={formatPrice}
          formatLineAmount={formatLineAmount}
          discountCode={discountCode}
          onDiscountCodeChange={setDiscountCode}
          appliedDiscount={appliedDiscount}
          discountError={discountError}
          isValidatingDiscount={isValidatingDiscount}
          onValidateDiscount={validateDiscountCode}
          onRemoveDiscount={removeDiscountCode}
          discountAmount={calculateCurrentDiscountAmount()}
        />
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      )}

      <OfferSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        offerNumber={lastOfferNumber}
        customerEmail={formData.email}
      />
      <AlreadyCreatedModal
        isOpen={showAlreadyCreatedModal}
        onClose={() => setShowAlreadyCreatedModal(false)}
        onRestart={handleRestart}
      />
    </div>
  );
}
